import {
  Box,
  Chip,
  IconButton,
  Paper,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { sortBy } from 'lodash'
import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Factsheet_User, Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { AddNewUserFactsheetModalDialog } from 'src/screens/factsheets/factsheet/permissions/AddNewUserFactsheetDialog'
import {
  deleteUserFromFactsheet,
  queryFactsheetUsers,
  updateFactsheetUser,
} from 'src/screens/factsheets/factsheet/permissions/factsheetPermissionsQuery'
import { resolveUserPermissionMessageKey } from 'src/screens/shared/project/permissions/ProjectPermissionsUtils'
import { usePermissionsForFactsheetUsers } from 'src/service/security/PermissionHook'
import { AddButton } from 'src/shared/button/Buttons'
import { FACTSHEET_USER_ROLES, USER_STATUS } from 'src/shared/constants/constants'
import { DeleteIcon, EditIcon, PersonAddIcon, PersonIcon } from 'src/shared/icons/Icons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { FactsheetExportMenu } from 'src/shared/menu/FactsheetExportMenu'
import { ConfirmationModalDialog } from 'src/shared/modal-dialog/ConfirmationModalDialog'
import { ModalDialog } from 'src/shared/modal-dialog/ModalDialog'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { S } from 'src/shared/styled/S'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { UserContext } from 'src/user/UserContext'
import styled from 'styled-components'
import { useClient } from 'urql'
import { EditUserPermissionModal } from 'src/screens/shared/permission-users/EditUserPermissionModal'
import { Option } from 'src/shared/form/control'

const StyledTableRow = styled(TableRow)`
  & .MuiTableCell-root {
    padding-top: ${({ theme }) => theme.spacing(1.5)};
    padding-bottom: ${({ theme }) => theme.spacing(1.5)};
  }
`

const StyledTableCell = styled(TableCell)<{ $active?: boolean }>`
  color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
`

const StyledChip = styled(Chip)<{ $active: boolean } & SvgIconProps>`
  background-color: ${({ $active, theme }) => ($active ? theme.colors.success.light : theme.colors.grey.background)};
  color: ${({ $active, theme }) => ($active ? theme.colors.success.dark : theme.colors.text.secondary)};
`

export const FactsheetPermissionsPage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const { user } = useContext(UserContext)
  const urqlClient = useClient()
  const { factsheetId } = useParams()
  const factsheetIdNumber = parseInt(factsheetId as string)
  const notificationService = useNotificationService()

  const [factsheetUsers, setFactsheetUsers] = useState<Factsheet_User[]>([])
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false)
  const [openEditUserDialog, setOpenEditUserDialog] = useState(false)
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false)
  const [selectedUserForAction, setSelectedUserForAction] = useState<Factsheet_User | null>(null)

  const factsheetPermissionOptions = useMemo<Option[]>(() => {
    return [
      { label: getMessage('label.can.edit'), value: FACTSHEET_USER_ROLES.CONTRIBUTOR },
      { label: getMessage('label.can.view'), value: FACTSHEET_USER_ROLES.READER },
    ]
  }, [getMessage])

  const { loading, canView, canModifyFactsheetUsers } = usePermissionsForFactsheetUsers(factsheetIdNumber)

  const isUserActive = (user: Factsheet_User): boolean => user.user.status === USER_STATUS.ACTIVE

  const fetchUsers = useCallback(async () => {
    const { data, error } = await urqlClient
      .query<
        {
          factsheetUsers: Query_Root['factsheet_by_pk']
        },
        {
          id: number
        }
      >(queryFactsheetUsers, { id: factsheetIdNumber })
      .toPromise()

    if (error) {
      notificationService.operationFailed()
      return
    }

    const fetchedFactsheetUsers = data?.factsheetUsers?.factsheet_users ?? []

    setFactsheetUsers(sortBy(fetchedFactsheetUsers, [(pu: Factsheet_User) => pu.type !== FACTSHEET_USER_ROLES.ADMIN]))
  }, [factsheetIdNumber, urqlClient, notificationService])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // add action
  const handleOpenAddNewUserDialog = () => {
    setOpenAddUserDialog(true)
  }

  const handleCloseAddUserDialog = () => {
    setOpenAddUserDialog(false)
    setSelectedUserForAction(null)
  }

  const handleAfterSuccessAddUser = async () => {
    handleCloseAddUserDialog()
    await fetchUsers()
  }

  // delete action
  const handleOpenDeleteUserDialog = (user: Factsheet_User) => () => {
    setSelectedUserForAction(user)
    setOpenDeleteUserDialog(true)
  }
  const handleCloseDeleteUserDialog = () => {
    setOpenDeleteUserDialog(false)
    setSelectedUserForAction(null)
  }

  const handleDeleteUserConfirmed = async () => {
    const { data, error } = await urqlClient
      .mutation<{ delete_factsheet_user: Mutation_Root['delete_factsheet_user'] }>(deleteUserFromFactsheet, {
        factsheetId: factsheetId,
        userId: selectedUserForAction?.user?.id,
      })
      .toPromise()

    if (error || data?.delete_factsheet_user?.affected_rows === 0) {
      notificationService.operationFailed()
    } else {
      notificationService.deleteSuccessful()
      await fetchUsers()
    }

    handleCloseDeleteUserDialog()
  }

  // edit action
  const handleOpenEditUserDialog = (user: Factsheet_User) => () => {
    setSelectedUserForAction(user)
    setOpenEditUserDialog(true)
  }

  const handleCloseEditUserDialog = () => {
    setOpenEditUserDialog(false)
    setSelectedUserForAction(null)
  }

  const handleEditUserPermission = async (value: string) => {
    const { data, error } = await urqlClient
      .mutation<{ update_factsheet_user: Mutation_Root['update_factsheet_user'] }>(updateFactsheetUser, {
        factsheetUserId: selectedUserForAction?.id,
        type: value,
      })
      .toPromise()

    if (error || data?.update_factsheet_user?.affected_rows === 0) {
      notificationService.operationFailed()
    } else {
      notificationService.changesSaved()
      await fetchUsers()
    }

    handleCloseEditUserDialog()
  }

  if (!loading && !canView) {
    return <NotAuthorized />
  }

  return (
    <>
      {!loading && (
        <ScreenLayout
          title={getMessage('label.navigation.factsheets.permissions')}
          hasSecondLevelNavigation={false}
          actions={<FactsheetExportMenu />}
        >
          <PageLayout>
            {!loading && (
              <Section
                title={getMessage('label.project.permissions.users')}
                actionButton={
                  canModifyFactsheetUsers && (
                    <AddButton
                      messageKey="button.user.add"
                      startIcon={<PersonAddIcon />}
                      onClick={handleOpenAddNewUserDialog}
                    />
                  )
                }
                helpAndInstructions={<HelpAndInstructions labelKey="label.help.factsheet.permissions" />}
              >
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{getMessage('label.name')}</TableCell>
                        <TableCell>{getMessage('label.permissions')}</TableCell>
                        <TableCell>{getMessage('label.status')}</TableCell>
                        {canModifyFactsheetUsers && <TableCell align="right">{getMessage('label.actions')}</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {factsheetUsers.map((u, i) => (
                        <StyledTableRow key={i}>
                          <StyledTableCell $active={isUserActive(u)}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <S.Avatar $randomColorKey={isUserActive(u) ? u?.user.email : ''}>
                                {isUserActive(u) ? u.user.first_name?.charAt(0) : <PersonIcon />}
                              </S.Avatar>
                              <S.DataGrid.TwoLineCell>
                                <Typography variant="subtitle2">
                                  {isUserActive(u)
                                    ? `${u.user.first_name} ${u.user.last_name}`
                                    : getMessage('label.user.unknown')}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {u.user.email}
                                </Typography>
                              </S.DataGrid.TwoLineCell>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell $active={isUserActive(u)}>
                            {getMessage(resolveUserPermissionMessageKey(u.type))}
                          </StyledTableCell>
                          <StyledTableCell $active={isUserActive(u)}>
                            <StyledChip
                              $active={isUserActive(u)}
                              label={getMessage(`label.${u.user.status.toLowerCase()}`)}
                            />
                          </StyledTableCell>

                          {canModifyFactsheetUsers && (
                            <StyledTableCell align="right">
                              <Box display={'flex'} gap={1} justifyContent={'end'}>
                                {user.email !== u.user.email && u.type !== FACTSHEET_USER_ROLES.ADMIN && (
                                  <IconButton color="primary" onClick={handleOpenEditUserDialog(u)}>
                                    <EditIcon />
                                  </IconButton>
                                )}

                                {user.email !== u.user.email && u.type !== FACTSHEET_USER_ROLES.ADMIN && (
                                  <IconButton color="primary" onClick={handleOpenDeleteUserDialog(u)}>
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </StyledTableCell>
                          )}
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {factsheetIdNumber && (
                  <ModalDialog
                    open={openAddUserDialog}
                    title={getMessage('label.add.new.user')}
                    maxWidth="sm"
                    withCloseIcon={false}
                    onClose={handleCloseAddUserDialog}
                  >
                    <AddNewUserFactsheetModalDialog
                      onCancel={handleCloseAddUserDialog}
                      onSuccess={handleAfterSuccessAddUser}
                      factsheetId={factsheetIdNumber}
                    />
                  </ModalDialog>
                )}

                <ConfirmationModalDialog
                  open={openDeleteUserDialog}
                  onCancel={handleCloseDeleteUserDialog}
                  onConfirm={handleDeleteUserConfirmed}
                >
                  {getMessage('label.delete.confirm')}
                </ConfirmationModalDialog>

                {selectedUserForAction && (
                  <ModalDialog
                    maxWidth="sm"
                    open={openEditUserDialog}
                    onClose={handleCloseEditUserDialog}
                    title={getMessage('label.edit.user.permission')}
                  >
                    <EditUserPermissionModal
                      userPermissionsOptions={factsheetPermissionOptions}
                      userPermission={selectedUserForAction?.type}
                      onConfirm={handleEditUserPermission}
                      onCancel={handleCloseEditUserDialog}
                    />
                  </ModalDialog>
                )}
              </Section>
            )}
          </PageLayout>
        </ScreenLayout>
      )}
    </>
  )
}
